import { stringUtils } from "cayo.ui";
import { Alert } from "@cayo/shared/api/types/cayo-graph";
import { IAlertItem, ODataArrayResponse } from "@cayo/shared/components/Alerts/types";
import logger from "@cayo/shared/libs/logger";
import { parseResponse } from "@cayo/shared/utils/ajax";
import { urlUtils } from "@cayo/shared/utils/url";

const log = logger.getLogger("AlertsClient");

class AlertsClient {
  constructor(
    readonly baseUrl: string,
    readonly selectCountUrl: string,
    readonly selectAlertsUrl: string,
    readonly dismissAlertUrl: string,
    readonly dismissAllAlertsUrl?: string,
    readonly processResponseFunction?: (response: any[]) => void
  ) {
    this.dismissAlert = this.dismissAlert.bind(this);
    this.dismissAll = this.dismissAll.bind(this);
    if (log.isDebugEnabled()) {
      log.debug(".ctor -> args", { baseUrl, selectCountUrl, selectAlertsUrl, dismissAlertUrl });
    }
  }

  public dismissAlert(item: IAlertItem): Promise<void> {
    const url = stringUtils.format(this.dismissAlertUrl, [item.id!]);

    const content = JSON.stringify({ status: "dismissed" });

    return fetch(url, {
      body: content,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "PATCH",
    }).then(() => {
      return;
    });
  }

  public dismissAll(alertTypes: string[] | undefined, exclude?: boolean): Promise<void> {
    log.debug("dismissAll -> alertType", alertTypes);
    const content = alertTypes?.length
      ? JSON.stringify(
          exclude
            ? { excludedAlertTypes: alertTypes, hiddenOnly: true }
            : { alertTypes, hiddenOnly: true }
        )
      : undefined;

    return fetch(this.dismissAllAlertsUrl!, {
      body: content,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "POST",
    }).then(() => {
      return;
    });
  }

  public getNewAlerts(): Promise<{ alerts: IAlertItem[]; errors: number }> {
    return fetch(this.getAlertsUrl(this.selectAlertsUrl), {
      headers: {},
    }).then((response) => {
      return response
        .text()
        .then((responseText) => {
          const result = parseResponse<ODataArrayResponse<IAlertItem>>(responseText);

          if (this.processResponseFunction && result?.value) {
            this.processResponseFunction(result.value);
          }

          const alerts = result?.value ?? [];
          alerts?.forEach((a: any) => {
            const modificationTime = (a as any as Alert).lastModifiedDateTime;
            a.modificationTime = modificationTime ? new Date(modificationTime) : new Date();
          });

          return {
            alerts: alerts,
            errors: AlertsClient.countErrors(alerts),
          };
        })
        .catch((e) => {
          logger.log(e);

          return { alerts: [], errors: 0 };
        });
    });
  }

  public getNewAlertsCount(): Promise<{ count: number; errors: number }> {
    return fetch(this.getAlertsUrl(this.selectCountUrl), {
      headers: {},
    }).then((response) => {
      return response.text().then((responseText) => {
        const result = parseResponse<ODataArrayResponse<IAlertItem>>(responseText);

        const alerts = result && result.value && (result.value as any as IAlertItem[]);
        alerts?.forEach((a: any) => {
          const modificationTime = (a as any as Alert).lastModifiedDateTime;
          a.modificationTime = modificationTime ? new Date(modificationTime) : new Date();
        });

        return {
          count: alerts?.length ?? 0,
          // tslint:disable-next-line: triple-equals
          errors: alerts ? AlertsClient.countErrors(alerts) : 0,
        };
      });
    });
  }

  public static countErrors(items: IAlertItem[]) {
    let sum: number = 0;
    items.forEach((a) => {
      if (a.severity === "high" || a.severity === "error" || a.severity === "critical") {
        sum++;
      }
    });

    return sum;
  }

  public static countWarnings(items: IAlertItem[]) {
    let sum: number = 0;
    items.forEach((a) => {
      if (a.severity === "warning" || a.severity === "medium") {
        sum++;
      }
    });

    return sum;
  }

  public static countNew(items: IAlertItem[]) {
    let sum: number = 0;
    items.forEach((a) => {
      if (!a.disappeared) {
        sum++;
      }
    });

    return sum;
  }

  private getAlertsUrl = (url: string) => urlUtils.addRequestTime(url);
}

export default AlertsClient;
